import React from 'react'
import { t } from 'ttag'
import YumGroupSelect from '../YumGroupSelect/YumGroupSelect'
import {
  CompanyId,
  DivisionId,
  DivisionRow,
  UserId,
  DivisionCategoryRow,
  DivisionDto,
  DivisionCategoryType,
} from '../../services/api.types'
import CreateTeamWrapper from '../../views/TeamManagement/components/CreateTeam/CreateTeamWrapper'
import DeleteTeamWrapper from '../../views/TeamManagement/components/DeleteTeam/DeleteTeamWrapper'
import ErrorMessage from '../ErrorMessage/ErrorMessage'
import { translateDivisionCategoryType } from '../../views/TeamManagement/TeamManagement'

interface GroupSelectionUIProps {
  companyId: CompanyId
  userId: UserId
  category: DivisionCategoryRow
  groups: DivisionDto[]
  selectedGroupId: DivisionId | null
  onGroupChange: (categoryId: string) => (event: React.ChangeEvent<{ value: unknown }>) => void
  showIcon?: boolean
  useAutocompleteSelect?: boolean
  isDuringRegistration?: boolean
  error?: boolean
  errorMessage?: string
  disabled?: boolean
}

/**
 * GroupSelectionUI component
 *
 * A reusable component for group selection interface that can be used for different category types.
 *
 * Features:
 * - Shows group selection dropdown with available groups for a category
 * - Allows creating a new group if group creation is enabled
 * - Allows deleting a group created by the user
 * - Handles group selection restrictions based on company settings
 * - Shows appropriate error messages for validation errors
 */
const GroupSelectionUI: React.FC<GroupSelectionUIProps> = ({
  companyId,
  userId,
  category,
  groups,
  selectedGroupId,
  onGroupChange,
  showIcon = false,
  useAutocompleteSelect = false,
  isDuringRegistration = false,
  error = false,
  errorMessage: errorMessageProp,
  disabled = false,
}) => {
  const groupSwitchingDisabled = category.switchingDisabled ?? false
  const groupSelectionLocked = category.selectionLocked ?? false
  const groupSelectionRequired = category.required ?? false
  const groupLocked = !selectedGroupId ? false : groups.find((g) => g.id === selectedGroupId)?.locked || false

  // Note: DivisionRow doesn't have creatorUserId, so we can't identify user-created groups
  // This functionality may need to be implemented differently or removed
  const createdGroupId = groups.find((g) => g.creatorUserId === userId)?.id
  const createdGroupMembers = groups.find((g) => g.creatorUserId === userId)?.members

  // Derive settings from category object
  const allowGroupCreation = category.usersCanCreate ?? true // Default to true if not specified
  const maxGroupSize = category.maxSize

  // User can create teams if:
  // 1. Group creation is allowed AND
  // 2. Either user has no group OR group switching is not disabled OR
  // 3. User's current group is not locked
  const canCreateGroups = allowGroupCreation && (!groupSwitchingDisabled || !selectedGroupId) && !groupSelectionLocked

  if (!groups.length && !canCreateGroups) {
    return null
  }

  // Even if there are no teams, we may need to show "Create Team".
  const showGroupSelect = (groups && groups.length && !groupSelectionLocked) || canCreateGroups

  // Default error message for group selection
  const errorMessage = errorMessageProp || t`Group selection is required.`

  return (
    <div>
      {/* <div>
        <b>{translateDivisionCategoryType(category)}</b>
      </div>
      <div>selectedGroupId: {selectedGroupId}</div>
      <div>createdGroupId: {createdGroupId}</div>
      <div>{groupSelectionLocked ? 'only admins can assign groups' : 'users can select groups'}</div>
      <div>{canCreateGroups ? 'can create groups' : 'cannot create groups'}</div>
      <div>{groupLocked ? 'group is locked' : 'group is not locked'}</div>
      <div>{groupSelectionRequired ? 'group selection is required' : 'group selection is not required'}</div> */}
      {showGroupSelect && (
        <div
          style={{
            marginBottom: canCreateGroups || !isDuringRegistration ? 0 : '32px',
          }}
        >
          <YumGroupSelect
            disabled={
              (groupSwitchingDisabled && !!category.id) ||
              groupSelectionLocked ||
              groupLocked ||
              (!!createdGroupId && !!selectedGroupId && createdGroupId === selectedGroupId) ||
              disabled
            }
            label={translateDivisionCategoryType(category)}
            groups={groups}
            value={selectedGroupId || ''}
            onChange={onGroupChange(category.id)}
            required={groupSelectionRequired}
            error={error}
            showIcon={showIcon}
            useAutocompleteSelect={useAutocompleteSelect}
            maxGroupSize={maxGroupSize}
            style={{
              marginBottom: 0,
            }}
            isDuringRegistration={isDuringRegistration}
          />
          {error && <ErrorMessage>{errorMessage}</ErrorMessage>}
          {(groupSwitchingDisabled && !!selectedGroupId) || groupSelectionLocked || groupLocked ? (
            <div
              style={{
                fontSize: '12px',
                opacity: 0.5,
                marginTop: 3,
              }}
            >
              {groupSwitchingDisabled && !!selectedGroupId
                ? t`Group switching is disabled`
                : groupSelectionLocked
                  ? t`Group selection is locked`
                  : groupLocked
                    ? t`This group is locked`
                    : null}
            </div>
          ) : null}
        </div>
      )}
      {canCreateGroups && (
        <div
          style={{
            marginBottom: !isDuringRegistration ? 0 : '38px',
          }}
        >
          {!createdGroupId ? (
            <CreateTeamWrapper
              variant="link"
              text={t`Create new group`}
              companyId={companyId}
              isUserCreated={true}
              isDuringRegistration={true}
              selectedGroupId={category.id}
              style={{
                marginBottom: '0px',
              }}
            />
          ) : isDuringRegistration || !groupSelectionRequired || !createdGroupMembers || createdGroupMembers <= 1 ? (
            <DeleteTeamWrapper
              text={t`Delete group`}
              divisionId={createdGroupId}
              companyId={companyId}
              members={createdGroupMembers}
              isDuringRegistration={true}
              style={{
                marginBottom: '0px',
              }}
            />
          ) : (
            <div
              style={{
                fontSize: '12px',
                opacity: 0.5,
                marginTop: 3,
              }}
            >
              {t`Groups with members can’t be deleted`}
            </div>
          )}
          {groups.length === 0 && error ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
        </div>
      )}
    </div>
  )
}

export default GroupSelectionUI
